import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { mensajeDeError } from './Gastos';
import {
  T, serif, fmt, ROL_TEXTO, Card, Chip, Boton, Campo, estiloInput,
  Titulo, Aviso, Vacio, Cargando,
} from '../lib/ui';

// ════════════════════════════════════════════════════════════════════
// APROBAR
// ════════════════════════════════════════════════════════════════════
export function Aprobar({ usuario }) {
  const [gastos, setGastos] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [nota, setNota] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);

  // Antes se ocultaban los gastos propios, y quedaban invisibles sin
  // explicacion. Ahora se muestran aparte: la direccion puede
  // aprobarlos, pero viendo que son suyos.
  const cargar = () => api.gastos({ estado: 'enviado' })
    .then((r) => setGastos(r.gastos || []))
    .catch((err) => {
      setAviso({ tipo: 'bad', texto: mensajeDeError(err) });
      setGastos([]);
    });

  useEffect(() => { cargar(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const decidir = async (g, decision) => {
    if (decision !== 'aprobar' && !nota.trim()) {
      return setAviso({ tipo: 'warn', texto: 'Explica el motivo para que el rindente pueda corregirlo.' });
    }
    setOcupado(true);
    try {
      const r = await api.decidirGasto(g.id, decision, nota.trim() || undefined);
      setAbierto(null); setNota('');
      setAviso(r.avisoSap
        ? { tipo: 'warn', texto: `Aprobado, pero no entró a la cola de SAP: ${r.avisoSap}` }
        : { tipo: 'ok', texto: 'Listo.' });
      await cargar();
    } catch (err) {
      setAviso({
        tipo: 'bad',
        texto: err.message === 'monto_fuera_de_facultad'
          ? 'Ese monto excede tu facultad de aprobación. Debe aprobarlo la dirección.'
          : 'No se pudo registrar la decisión.',
      });
    }
    setOcupado(false);
  };

  if (!gastos) return <Cargando />;

  // Los gastos propios se separan de los del equipo. El servidor deja
  // que la direccion apruebe los suyos, pero conviene que se vea que
  // lo son: es una excepcion al control, no la norma.
  const esDirector = usuario.rol === 'director';
  const propios = gastos.filter((g) => g.usuarioId === usuario.id);
  const ajenos = gastos.filter((g) => g.usuarioId !== usuario.id);
  const ordenados = esDirector ? [...ajenos, ...propios] : ajenos;

  return (
    <>
      <Titulo sub="Responde dentro de 24 horas">Aprobar gastos</Titulo>
      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {esDirector && propios.length > 0 && (
        <Aviso tipo="warn">
          {propios.length === 1
            ? 'Uno de estos gastos es tuyo. '
            : `${propios.length} de estos gastos son tuyos. `}
          Puedes aprobarlos porque eres dirección, pero queda registrado
          como auto-aprobación. Lo apropiado es que otro director los revise.
        </Aviso>
      )}

      {ajenos.length === 0 && propios.length === 0
        ? <Vacio>No hay gastos esperando tu decisión.</Vacio>
        : (
          <div style={{ display: 'grid', gap: 10 }}>
            {ordenados.map((g) => (
              <Card key={g.id} style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    {g.usuarioId === usuario.id && (
                      <span style={{
                        display: 'inline-block', marginBottom: 5,
                        fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
                        padding: '2px 8px', borderRadius: 9,
                        background: T.warnBg, color: T.warn,
                      }}>
                        GASTO TUYO
                      </span>
                    )}
                    {/* Quién lo capturó: es lo primero que necesita saber
                        quien aprueba, así que va arriba y destacado. */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 7,
                      marginBottom: 6,
                    }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: T.navy, color: T.brass,
                        fontSize: 10, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0,
                      }}>
                        {iniciales(g.autorNombre)}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
                        {g.autorNombre || 'Sin identificar'}
                      </span>
                      {g.autorCentro && (
                        <span style={{ fontSize: 11, color: T.muted }}>
                          · {g.autorCentro}
                        </span>
                      )}
                      {/* Quien rinde no siempre es quien gastó: Dulce
                          administra el encargo y el técnico consume. Sin
                          esto el aprobador no sabe a quién le está
                          aprobando el consumo. */}
                      {g.consumidor && (
                        <span style={{
                          fontSize: 11, color: T.navy, background: T.warm,
                          padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                        }}>
                          lo gastó {g.consumidor}
                        </span>
                      )}
                    </div>

                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      {g.concepto || g.proveedorRazon || 'Gasto'}
                    </div>
                    <div style={{ color: T.muted, fontSize: 12, marginTop: 3 }}>
                      {fmt.fecha(g.fecha)}
                      {g.ovId && ` · ${g.ovId}`}
                      {g.proveedorRazon && ` · ${g.proveedorRazon}`}
                    </div>
                    <div style={{ color: T.muted, fontSize: 12 }}>
                      Cuenta {g.cuentaPcge}
                      {g.comprobanteSerie && ` · ${g.comprobanteSerie}-${g.comprobanteCorrelativo}`}
                    </div>
                    {g.proveedorCondicion === 'no_habido' && (
                      <div style={{ fontSize: 12, color: T.bad, marginTop: 6, fontWeight: 600 }}>
                        El proveedor figura como NO HABIDO en SUNAT: este
                        comprobante no da crédito fiscal.
                      </div>
                    )}
                    {g.proveedorEstado && g.proveedorEstado !== 'activo' && (
                      <div style={{ fontSize: 12, color: T.bad, marginTop: 6, fontWeight: 600 }}>
                        El RUC del proveedor no está activo en SUNAT.
                      </div>
                    )}
                    {g.rucValido === false && (
                      <div style={{ fontSize: 12, color: T.bad, marginTop: 6, fontWeight: 600 }}>
                        El RUC no es un número válido. Revísalo antes de aprobar.
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontFamily: serif, fontSize: 22, fontWeight: 600,
                    color: T.navy, whiteSpace: 'nowrap',
                  }}>
                    {fmt.money(g.total, g.moneda)}
                  </div>
                </div>

                {abierto === g.id ? (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                    <Campo etiqueta="Nota (obligatoria si observas o rechazas)">
                      <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
                                placeholder="Qué debe corregir el rindente"
                                style={{ ...estiloInput, resize: 'vertical' }} />
                    </Campo>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Boton variante="exito" disabled={ocupado}
                             onClick={() => decidir(g, 'aprobar')}>Aprobar</Boton>
                      <Boton variante="suave" disabled={ocupado}
                             onClick={() => decidir(g, 'observar')}>Observar</Boton>
                      <Boton variante="peligro" disabled={ocupado}
                             onClick={() => decidir(g, 'rechazar')}>Rechazar</Boton>
                      <Boton variante="suave"
                             onClick={() => { setAbierto(null); setNota(''); }}>Cancelar</Boton>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <Boton variante="suave" onClick={() => setAbierto(g.id)}>Revisar</Boton>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// MI EQUIPO
// ════════════════════════════════════════════════════════════════════
export function Equipo({ usuario }) {
  const [equipo, setEquipo] = useState(null);
  const [encargos, setEncargos] = useState([]);

  useEffect(() => {
    Promise.all([api.equipo(), api.encargos(true)])
      .then(([e, en]) => { setEquipo(e.equipo); setEncargos(en.encargos); })
      .catch(() => setEquipo([]));
  }, []);

  if (!equipo) return <Cargando />;

  const otros = equipo.filter((u) => u.id !== usuario.id);

  return (
    <>
      <Titulo sub="Cumplimiento de las personas a tu cargo">Mi equipo</Titulo>

      {otros.length === 0
        ? <Vacio>No tienes personas asignadas.</Vacio>
        : (
          <div style={{ display: 'grid', gap: 10 }}>
            {otros.map((m) => {
              const suyos = encargos.filter((e) => e.usuarioId === m.id);
              const saldo = suyos.reduce((s, e) => s + (e.saldo || 0), 0);
              const vencidos = suyos.filter(
                (e) => e.vencimiento && new Date(e.vencimiento) < new Date(),
              ).length;

              return (
                <Card key={m.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{m.nombre}</div>
                      <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                        {ROL_TEXTO[m.rol]} · {m.centro}
                      </div>
                      <div style={{ color: T.muted, fontSize: 11, marginTop: 4 }}>
                        Último ingreso: {fmt.fechaHora(m.ultimoLogin)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: serif, fontSize: 20, fontWeight: 600, color: T.navy }}>
                        {fmt.money(saldo)}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted }}>por rendir</div>
                      {vencidos > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <Chip estado="error">{vencidos} vencido{vencidos > 1 ? 's' : ''}</Chip>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// ENCARGOS
// ════════════════════════════════════════════════════════════════════
export function Encargos({ usuario }) {
  const [encargos, setEncargos] = useState(null);
  // Un encargo se le puede abrir a cualquiera de la empresa, no solo
  // al equipo de quien lo tramita.
  const [depositables, setDepositables] = useState([]);
  const [equipo, setEquipo] = useState([]);
  const [ovs, setOvs] = useState([]);
  const [nuevo, setNuevo] = useState(false);
  const [aviso, setAviso] = useState(null);
  const esDirector = usuario.rol === 'director';

  const cargar = () => api.encargos().then((r) => setEncargos(r.encargos)).catch(() => setEncargos([]));

  useEffect(() => {
    cargar();
    if (esDirector) {
      Promise.all([api.equipo(), api.ovs(true), api.depositables()])
        .then(([e, o, dep]) => {
          setEquipo(e.equipo);
          setOvs(o.ovs);
          setDepositables(dep?.personas || e.equipo || []);
        })
        .catch(() => {});
    }
  }, [esDirector]);

  if (!encargos) return <Cargando />;

  return (
    <>
      <Titulo sub={esDirector ? 'Asigna y controla las entregas a rendir' : 'Tus entregas a rendir'}>
        Encargos
      </Titulo>

      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {esDirector && (
        <div style={{ marginBottom: 16 }}>
          <Boton variante={nuevo ? 'suave' : 'acento'} onClick={() => setNuevo(!nuevo)}>
            {nuevo ? 'Cancelar' : 'Asignar encargo'}
          </Boton>
        </div>
      )}

      {nuevo && (
        <FormEncargo
          equipo={depositables.length ? depositables : equipo} ovs={ovs}
          onListo={async () => {
            setNuevo(false);
            setAviso({ tipo: 'ok', texto: 'Encargo asignado. La entrega se envió a la cola de SAP.' });
            await cargar();
          }}
          onError={() => setAviso({ tipo: 'bad', texto: 'No se pudo asignar el encargo.' })}
        />
      )}

      {encargos.length === 0
        ? <Vacio>No hay encargos registrados.</Vacio>
        : (
          <div style={{ display: 'grid', gap: 10 }}>
            {encargos.map((e) => {
              const vencido = e.vencimiento && new Date(e.vencimiento) < new Date() && !e.cerrado;
              return (
                <Card key={e.id} style={{ borderLeft: vencido ? `3px solid ${T.bad}` : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{e.id}</div>
                      <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                        {e.glosa || e.tipo}{e.ovId ? ` · ${e.ovId}` : ''}
                      </div>
                      <div style={{ fontSize: 11, marginTop: 4, color: vencido ? T.bad : T.muted }}>
                        Entregado {fmt.money(e.entregado, e.moneda)} ·
                        rendido {fmt.money(e.rendido, e.moneda)}
                        {e.vencimiento && ` · ${vencido ? 'venció' : 'vence'} ${fmt.fecha(e.vencimiento)}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{
                        fontFamily: serif, fontSize: 20, fontWeight: 600,
                        color: e.saldo > 0 ? T.navy : T.ok,
                      }}>
                        {fmt.money(e.saldo, e.moneda)}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted }}>saldo</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </>
  );
}

function FormEncargo({ equipo, ovs, onListo, onError }) {
  const [f, setF] = useState({
    usuarioId: '', tipo: 'mantenimiento', monto: '', moneda: 'PEN',
    glosa: '', ovId: '', vencimiento: '',
  });
  const [ocupado, setOcupado] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const enviar = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      await api.asignarEncargo({
        usuarioId: f.usuarioId, tipo: f.tipo, monto: parseFloat(f.monto),
        moneda: f.moneda, glosa: f.glosa || undefined,
        ovId: f.ovId || null, vencimiento: f.vencimiento || null,
      });
      onListo();
    } catch { onError(); setOcupado(false); }
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <form onSubmit={enviar}>
        <Campo etiqueta="Asignar a">
          <select value={f.usuarioId} onChange={(e) => set('usuarioId', e.target.value)}
                  required style={estiloInput}>
            <option value="">Elegir persona…</option>
            {equipo.map((u) => (
              <option key={u.id} value={u.id}>{u.nombre} — {ROL_TEXTO[u.rol]}</option>
            ))}
          </select>
        </Campo>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
          <Campo etiqueta="Monto">
            <input inputMode="decimal" value={f.monto} required
                   onChange={(e) => set('monto', e.target.value)} style={estiloInput} />
          </Campo>
          <Campo etiqueta="Moneda">
            <select value={f.moneda} onChange={(e) => set('moneda', e.target.value)}
                    style={estiloInput}>
              <option value="PEN">PEN</option>
              <option value="USD">USD</option>
            </select>
          </Campo>
        </div>

        <Campo etiqueta="Orden de venta">
          <select value={f.ovId} onChange={(e) => set('ovId', e.target.value)} style={estiloInput}>
            <option value="">Sin OV</option>
            {ovs.map((o) => <option key={o.id} value={o.id}>{o.id} — {o.clienteAlias}</option>)}
          </select>
        </Campo>

        <Campo etiqueta="Glosa">
          <input value={f.glosa} onChange={(e) => set('glosa', e.target.value)}
                 placeholder="Viáticos mantenimiento ZACT" style={estiloInput} />
        </Campo>

        <Campo etiqueta="Vence el" ayuda="Se avisa al rindente 3 días antes y el día del vencimiento.">
          <input type="date" value={f.vencimiento}
                 onChange={(e) => set('vencimiento', e.target.value)} style={estiloInput} />
        </Campo>

        <Boton type="submit" variante="acento" disabled={ocupado} style={{ width: '100%' }}>
          {ocupado ? 'Asignando…' : 'Asignar encargo'}
        </Boton>
      </form>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// ÓRDENES DE VENTA
// ════════════════════════════════════════════════════════════════════
export function Ovs({ usuario }) {
  const [ovs, setOvs] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState(null);
  const esDirector = usuario.rol === 'director';

  const cargar = () => api.ovs().then((r) => setOvs(r.ovs)).catch(() => setOvs([]));
  useEffect(() => { cargar(); }, []);

  /**
   * Una OV con movimientos NO se borra: se anula. Borrarla dejaria
   * esos gastos sin proyecto y descuadraria lo ya enviado a SAP.
   */
  const borrar = async (ov) => {
    let uso;
    try {
      uso = await api.usoOv(ov.id);
    } catch {
      return setAviso({ tipo: 'bad', texto: 'No se pudo comprobar el uso de la OV.' });
    }

    if (uso.sePuedeBorrar) {
      if (!window.confirm(
        `¿Eliminar ${ov.id} definitivamente?\n\n` +
        'No tiene gastos, encargos ni viáticos asociados, así que se borra por completo.',
      )) return;

      try {
        await api.borrarOv(ov.id);
        setAviso({ tipo: 'ok', texto: `${ov.id} eliminada.` });
        await cargar();
      } catch {
        setAviso({ tipo: 'bad', texto: 'No se pudo eliminar.' });
      }
      return;
    }

    const detalle = [
      uso.gastos ? `${uso.gastos} gasto(s)` : null,
      uso.encargos ? `${uso.encargos} encargo(s)` : null,
      uso.viaticos ? `${uso.viaticos} solicitud(es) de viáticos` : null,
    ].filter(Boolean).join(', ');

    const motivo = window.prompt(
      `${ov.id} tiene ${detalle}.\n\n` +
      'No se puede eliminar sin dejar esos movimientos sin proyecto. ' +
      'Se puede ANULAR: deja de aparecer para nuevos gastos, pero conserva el historial.' +
      (uso.hayDocumentosEnSap
        ? `\n\nOJO: ${uso.enSap} gasto(s) ya están en SAP. Anular aquí no los quita de allá.`
        : '') +
      '\n\nEscribe el motivo de la anulación:',
    );
    if (!motivo || !motivo.trim()) return;

    try {
      await api.borrarOv(ov.id, motivo.trim());
      setAviso({ tipo: 'ok', texto: `${ov.id} anulada.` });
      await cargar();
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudo anular.' });
    }
  };

  if (!ovs) return <Cargando />;

  return (
    <>
      <Titulo sub="Los gastos se imputan al proyecto a través de la OV">
        Órdenes de venta
      </Titulo>

      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {esDirector && (
        <div style={{ marginBottom: 16 }}>
          <Boton variante={nuevo ? 'suave' : 'acento'} onClick={() => setNuevo(!nuevo)}>
            {nuevo ? 'Cancelar' : 'Nueva OV'}
          </Boton>
        </div>
      )}

      {nuevo && <FormOv onListo={async () => { setNuevo(false); await cargar(); }} />}

      <div style={{ display: 'grid', gap: 10 }}>
        {ovs.map((o) => (
          <Card key={o.id} style={{ opacity: o.estado === 'anulada' ? 0.55 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{o.id}</span>
                  <Chip estado={o.estado === 'abierta' ? 'aprobado' : 'borrador'}>
                    {o.estado}
                  </Chip>
                </div>
                <div style={{ fontSize: 14, marginTop: 4 }}>{o.clienteAlias}</div>
                <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                  {o.descripcion} · {o.centro}
                </div>
              </div>
              <div style={{
                fontFamily: serif, fontSize: 18, fontWeight: 600,
                color: T.navy, whiteSpace: 'nowrap',
              }}>
                {fmt.money(o.montoContrato, o.moneda)}
              </div>
            </div>

            {o.motivoAnulacion && (
              <div style={{ fontSize: 12, color: T.bad, marginTop: 8 }}>
                Anulada: {o.motivoAnulacion}
              </div>
            )}

            {esDirector && o.estado !== 'anulada' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Boton variante="suave" style={{ fontSize: 12, padding: '5px 11px' }}
                       onClick={() => setEditando(editando === o.id ? null : o.id)}>
                  {editando === o.id ? 'Cerrar' : 'Editar'}
                </Boton>
                <Boton variante="suave" style={{ fontSize: 12, padding: '5px 11px' }}
                       onClick={() => borrar(o)}>
                  Eliminar
                </Boton>
              </div>
            )}

            {editando === o.id && (
              <EditarOv
                ov={o}
                onListo={async () => {
                  setEditando(null);
                  setAviso({ tipo: 'ok', texto: 'Orden de venta actualizada.' });
                  await cargar();
                }}
                onError={(m) => setAviso({ tipo: 'bad', texto: m })}
              />
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function FormOv({ onListo }) {
  const [f, setF] = useState({
    id: '', clienteAlias: '', clienteRuc: '', descripcion: '', tipo: 'proyecto',
    montoContrato: '', moneda: 'USD', fechaInicio: new Date().toISOString().slice(0, 10),
    centro: 'LURIN',
  });
  const [ocupado, setOcupado] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const enviar = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      await api.crearOv({ ...f, montoContrato: parseFloat(f.montoContrato),
                          clienteRuc: f.clienteRuc || null });
      onListo();
    } catch { setOcupado(false); }
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <form onSubmit={enviar}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
          <Campo etiqueta="Código">
            <input value={f.id} required onChange={(e) => set('id', e.target.value.toUpperCase())}
                   placeholder="OV-2026-005" style={estiloInput} />
          </Campo>
          <Campo etiqueta="Centro">
            <input value={f.centro} required onChange={(e) => set('centro', e.target.value.toUpperCase())}
                   style={estiloInput} />
          </Campo>
        </div>
        <Campo etiqueta="Cliente">
          <input value={f.clienteAlias} required
                 onChange={(e) => set('clienteAlias', e.target.value)} style={estiloInput} />
        </Campo>
        <Campo etiqueta="Descripción">
          <input value={f.descripcion} onChange={(e) => set('descripcion', e.target.value)}
                 style={estiloInput} />
        </Campo>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 12 }}>
          <Campo etiqueta="Monto">
            <input inputMode="decimal" value={f.montoContrato} required
                   onChange={(e) => set('montoContrato', e.target.value)} style={estiloInput} />
          </Campo>
          <Campo etiqueta="Moneda">
            <select value={f.moneda} onChange={(e) => set('moneda', e.target.value)} style={estiloInput}>
              <option value="USD">USD</option><option value="PEN">PEN</option>
            </select>
          </Campo>
          <Campo etiqueta="Tipo">
            <select value={f.tipo} onChange={(e) => set('tipo', e.target.value)} style={estiloInput}>
              <option value="proyecto">Proyecto</option>
              <option value="mantenimiento">Mantenimiento</option>
              <option value="repuestos">Repuestos</option>
            </select>
          </Campo>
        </div>
        <Campo etiqueta="Inicio">
          <input type="date" value={f.fechaInicio}
                 onChange={(e) => set('fechaInicio', e.target.value)} style={estiloInput} />
        </Campo>
        <Boton type="submit" variante="acento" disabled={ocupado} style={{ width: '100%' }}>
          {ocupado ? 'Creando…' : 'Crear OV'}
        </Boton>
      </form>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// USUARIOS (sólo dirección)
// ════════════════════════════════════════════════════════════════════
export function Usuarios() {
  const [equipo, setEquipo] = useState(null);

  const [nuevo, setNuevo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState(null);

  const cargar = () => api.equipo().then((r) => setEquipo(r.equipo)).catch(() => setEquipo([]));
  useEffect(() => { cargar(); }, []);

  // Qué clave temporal quedó, para poder dársela a la persona. Antes
  // se restablecía y nadie sabía cuál decirle.
  const [temporal, setTemporal] = useState(null);
  // Diagnóstico de por qué alguien no puede entrar.
  const [acceso, setAcceso] = useState(null);

  const resetear = async (u) => {
    if (!window.confirm(`¿Restablecer la contraseña de ${u.nombre}?`)) return;
    try {
      const r = await api.resetPassword(u.id);
      setAviso(null);
      setTemporal({ nombre: u.nombre, usuario: u.username, ...r });
    } catch { setAviso({ tipo: 'bad', texto: 'No se pudo restablecer.' }); }
  };

  const diagnosticar = async (u) => {
    setAcceso({ cargando: true, nombre: u.nombre });
    try {
      setAcceso({ ...(await api.accesoUsuario(u.id)), nombre: u.nombre });
    } catch {
      setAcceso(null);
      setAviso({ tipo: 'bad', texto: 'No se pudo revisar el acceso.' });
    }
  };

  /**
   * La analitica cruza gastos de todo el personal y montos por
   * proyecto, asi que se concede persona a persona y no por rol.
   */
  const alternarReportes = async (u) => {
    const dar = !u.veReportes;
    if (dar && !window.confirm(
      `¿Dar acceso a Reportes a ${u.nombre}?\n\n` +
      'Podrá ver el gasto de todo el personal, los montos por proyecto ' +
      'y las desviaciones.',
    )) return;

    try {
      await api.editarUsuario(u.id, { veReportes: dar });
      setAviso({
        tipo: 'ok',
        texto: dar
          ? `${u.nombre} ya puede ver Reportes.`
          : `Se le quitó el acceso a Reportes a ${u.nombre}.`,
      });
      await cargar();
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudo cambiar el permiso.' });
    }
  };

  const alternar = async (u) => {
    try {
      await api.editarUsuario(u.id, { activo: !u.activo });
      await cargar();
    } catch { setAviso({ tipo: 'bad', texto: 'No se pudo actualizar.' }); }
  };

  if (!equipo) return <Cargando />;

  return (
    <>
      <Titulo sub="Altas, bajas, correos y restablecimiento de contraseñas">Usuarios</Titulo>
      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {temporal && (
        <ClaveTemporal d={temporal} onCerrar={() => setTemporal(null)} />
      )}
      {acceso && (
        <PorQueNoEntra d={acceso} onCerrar={() => setAcceso(null)} />
      )}

      {equipo.some((u) => u.email && !u.email.endsWith('@sicreaperu.com')) && (
        <Aviso tipo="warn">
          Hay correos que no son del dominio sicreaperu.com. Corrígelos con
          el botón Editar: las notificaciones por correo se envían a esa
          dirección.
        </Aviso>
      )}

      <div style={{ marginBottom: 16 }}>
        <Boton variante={nuevo ? 'suave' : 'acento'} onClick={() => setNuevo(!nuevo)}>
          {nuevo ? 'Cancelar' : 'Nuevo usuario'}
        </Boton>
      </div>

      {nuevo && (
        <FormUsuario
          equipo={equipo}
          onListo={async () => {
            setNuevo(false);
            setAviso({ tipo: 'ok', texto: 'Usuario creado con la contraseña inicial.' });
            await cargar();
          }}
        />
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {equipo.map((u) => (
          <Card key={u.id} style={{ opacity: u.activo ? 1 : 0.55 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap', alignItems: 'center',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {u.nombre} {!u.activo && <Chip estado="rechazado">Inactivo</Chip>}
                </div>
                <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                  {u.username} · {ROL_TEXTO[u.rol]} · {u.centro}
                  {u.veReportes && (
                    <span style={{
                      marginLeft: 7, fontSize: 10, fontWeight: 700,
                      padding: '1px 7px', borderRadius: 9,
                      background: `${T.brass}22`, color: T.brass,
                    }}>
                      VE REPORTES
                    </span>
                  )}
                </div>
                <div style={{
                  color: u.email && u.email.includes('@') ? T.muted : T.bad,
                  fontSize: 12, marginTop: 2,
                }}>
                  {u.email || 'sin correo'}
                  {u.whatsapp ? ` · ${u.whatsapp}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Boton variante="suave" onClick={() => setEditando(editando === u.id ? null : u.id)}
                       style={{ fontSize: 12, padding: '6px 12px' }}>
                  {editando === u.id ? 'Cerrar' : 'Editar'}
                </Boton>
                <Boton variante="suave" onClick={() => resetear(u)}
                       style={{ fontSize: 12, padding: '6px 12px' }}>
                  Restablecer clave
                </Boton>
                <Boton variante="suave" onClick={() => diagnosticar(u)}
                       style={{ fontSize: 12, padding: '6px 12px' }}>
                  ¿Por qué no entra?
                </Boton>
                <Boton variante="suave" onClick={() => alternar(u)}
                       style={{ fontSize: 12, padding: '6px 12px' }}>
                  {u.activo ? 'Desactivar' : 'Activar'}
                </Boton>
                <Boton variante="suave" onClick={() => alternarReportes(u)}
                       style={{ fontSize: 12, padding: '6px 12px' }}>
                  {u.veReportes ? 'Quitar reportes' : 'Dar reportes'}
                </Boton>
              </div>
            </div>

            {editando === u.id && (
              <EditarUsuario
                usuario={u} equipo={equipo}
                onListo={async () => {
                  setEditando(null);
                  setAviso({ tipo: 'ok', texto: 'Datos actualizados.' });
                  await cargar();
                }}
                onError={(m) => setAviso({ tipo: 'bad', texto: m })}
              />
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function FormUsuario({ equipo, onListo }) {
  const [f, setF] = useState({
    username: '', nombre: '', email: '', whatsapp: '',
    rol: 'operativo', reportaA: '', centro: 'LURIN', empresa: 'SICREA',
  });
  const [ocupado, setOcupado] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const enviar = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      await api.crearUsuario({ ...f, reportaA: f.reportaA || null, whatsapp: f.whatsapp || null });
      onListo();
    } catch { setOcupado(false); }
  };

  const jefes = equipo.filter((u) => ['mando_medio', 'director'].includes(u.rol));

  return (
    <Card style={{ marginBottom: 16 }}>
      <form onSubmit={enviar}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
          <Campo etiqueta="Usuario">
            <input value={f.username} required
                   onChange={(e) => set('username', e.target.value.toLowerCase())}
                   placeholder="nombre.apellido" style={estiloInput} />
          </Campo>
          <Campo etiqueta="Nombre completo">
            <input value={f.nombre} required
                   onChange={(e) => set('nombre', e.target.value)} style={estiloInput} />
          </Campo>
        </div>
        <Campo etiqueta="Correo">
          <input type="email" value={f.email} required
                 onChange={(e) => set('email', e.target.value)} style={estiloInput} />
        </Campo>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
          <Campo etiqueta="Rol">
            <select value={f.rol} onChange={(e) => set('rol', e.target.value)} style={estiloInput}>
              <option value="operativo">Operativo</option>
              <option value="mando_medio">Mando medio</option>
              <option value="contabilidad">Contabilidad</option>
              <option value="director">Dirección</option>
            </select>
          </Campo>
          <Campo etiqueta="Centro">
            <input value={f.centro} required
                   onChange={(e) => set('centro', e.target.value.toUpperCase())} style={estiloInput} />
          </Campo>
        </div>
        <Campo etiqueta="Reporta a" ayuda="Define qué mando medio ve sus gastos.">
          <select value={f.reportaA} onChange={(e) => set('reportaA', e.target.value)}
                  style={estiloInput}>
            <option value="">Nadie</option>
            {jefes.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        </Campo>
        <Boton type="submit" variante="acento" disabled={ocupado} style={{ width: '100%' }}>
          {ocupado ? 'Creando…' : 'Crear usuario'}
        </Boton>
      </form>
    </Card>
  );
}


function EditarUsuario({ usuario, equipo, onListo, onError }) {
  const [f, setF] = useState({
    nombre: usuario.nombre || '',
    email: usuario.email || '',
    whatsapp: usuario.whatsapp || '',
    rol: usuario.rol,
    centro: usuario.centro || '',
    reportaA: usuario.reportaA || '',
  });
  const [ocupado, setOcupado] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const guardar = async () => {
    setOcupado(true);
    try {
      await api.editarUsuario(usuario.id, {
        ...f,
        whatsapp: f.whatsapp || null,
        reportaA: f.reportaA || null,
      });
      onListo();
    } catch (err) {
      onError(err.status === 409 || err.message === 'email_duplicado'
        ? 'Ese correo ya está usado por otra persona.'
        : 'No se pudieron guardar los cambios.');
      setOcupado(false);
    }
  };

  const jefes = equipo.filter(
    (u) => u.id !== usuario.id && ['mando_medio', 'director'].includes(u.rol),
  );

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
      <Campo etiqueta="Nombre completo">
        <input value={f.nombre} onChange={(e) => set('nombre', e.target.value)}
               style={estiloInput} />
      </Campo>

      <Campo etiqueta="Correo" ayuda="A esta dirección llegan los avisos de la plataforma.">
        <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)}
               placeholder="inicial + apellido @sicreaperu.com" style={estiloInput} />
      </Campo>

      <Campo etiqueta="WhatsApp" ayuda="Opcional. Con código de país, por ejemplo +51987654321.">
        <input value={f.whatsapp} onChange={(e) => set('whatsapp', e.target.value)}
               style={estiloInput} />
      </Campo>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
        <Campo etiqueta="Rol">
          <select value={f.rol} onChange={(e) => set('rol', e.target.value)} style={estiloInput}>
            <option value="operativo">Operativo</option>
            <option value="mando_medio">Mando medio</option>
            <option value="contabilidad">Contabilidad</option>
            <option value="director">Dirección</option>
          </select>
        </Campo>
        <Campo etiqueta="Centro">
          <input value={f.centro} onChange={(e) => set('centro', e.target.value.toUpperCase())}
                 style={estiloInput} />
        </Campo>
      </div>

      <Campo etiqueta="Reporta a" ayuda="Define qué mando medio ve sus gastos.">
        <select value={f.reportaA} onChange={(e) => set('reportaA', e.target.value)}
                style={estiloInput}>
          <option value="">Nadie</option>
          {jefes.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
        </select>
      </Campo>

      <Boton onClick={guardar} disabled={ocupado} style={{ width: '100%' }}>
        {ocupado ? 'Guardando…' : 'Guardar cambios'}
      </Boton>
    </div>
  );
}


/**
 * Edicion de una OV. El codigo no se toca: es la clave con la que los
 * gastos ya capturados apuntan al proyecto.
 */
function EditarOv({ ov, onListo, onError }) {
  const [f, setF] = useState({
    clienteAlias: ov.clienteAlias || '',
    clienteRuc: ov.clienteRuc || '',
    descripcion: ov.descripcion || '',
    tipo: ov.tipo || 'proyecto',
    montoContrato: String(ov.montoContrato || ''),
    moneda: ov.moneda || 'USD',
    fechaInicio: (ov.fechaInicio || '').slice(0, 10),
    fechaFin: (ov.fechaFin || '').slice(0, 10),
    centro: ov.centro || '',
    numeroSap: ov.numeroSap || '',
    estado: ov.estado || 'abierta',
  });
  const [ocupado, setOcupado] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const guardar = async () => {
    setOcupado(true);
    try {
      await api.editarOv(ov.id, {
        ...f,
        montoContrato: Number(f.montoContrato) || 0,
        clienteRuc: f.clienteRuc || null,
        fechaFin: f.fechaFin || null,
        numeroSap: f.numeroSap || null,
      });
      onListo();
    } catch {
      onError('No se pudieron guardar los cambios.');
      setOcupado(false);
    }
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>
        El código {ov.id} no se puede cambiar: es la referencia de los gastos ya capturados.
      </div>

      <Campo etiqueta="Cliente">
        <input value={f.clienteAlias} onChange={(e) => set('clienteAlias', e.target.value)}
               style={estiloInput} />
      </Campo>
      <Campo etiqueta="RUC del cliente">
        <input value={f.clienteRuc} maxLength={11}
               onChange={(e) => set('clienteRuc', e.target.value.replace(/\D/g, ''))}
               style={estiloInput} />
      </Campo>
      <Campo etiqueta="Descripción">
        <input value={f.descripcion} onChange={(e) => set('descripcion', e.target.value)}
               style={estiloInput} />
      </Campo>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 12 }}>
        <Campo etiqueta="Monto">
          <input inputMode="decimal" value={f.montoContrato}
                 onChange={(e) => set('montoContrato', e.target.value)} style={estiloInput} />
        </Campo>
        <Campo etiqueta="Moneda">
          <select value={f.moneda} onChange={(e) => set('moneda', e.target.value)} style={estiloInput}>
            <option value="USD">USD</option><option value="PEN">PEN</option>
          </select>
        </Campo>
        <Campo etiqueta="Estado">
          <select value={f.estado} onChange={(e) => set('estado', e.target.value)} style={estiloInput}>
            <option value="abierta">Abierta</option>
            <option value="cerrada">Cerrada</option>
          </select>
        </Campo>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
        <Campo etiqueta="Inicio">
          <input type="date" value={f.fechaInicio}
                 onChange={(e) => set('fechaInicio', e.target.value)} style={estiloInput} />
        </Campo>
        <Campo etiqueta="Fin">
          <input type="date" value={f.fechaFin}
                 onChange={(e) => set('fechaFin', e.target.value)} style={estiloInput} />
        </Campo>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
        <Campo etiqueta="Centro">
          <input value={f.centro} onChange={(e) => set('centro', e.target.value.toUpperCase())}
                 style={estiloInput} />
        </Campo>
        <Campo etiqueta="N° en SAP">
          <input value={f.numeroSap} onChange={(e) => set('numeroSap', e.target.value)}
                 style={estiloInput} />
        </Campo>
      </div>

      <Boton onClick={guardar} disabled={ocupado} style={{ width: '100%' }}>
        {ocupado ? 'Guardando…' : 'Guardar cambios'}
      </Boton>
    </div>
  );
}


/** Iniciales para el círculo de quien capturó el gasto. */
function iniciales(nombre) {
  if (!nombre) return '?';
  const partes = String(nombre).trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}


// ── La clave temporal tras restablecer ──────────────────────────────
//
// Se muestra una sola vez, en pantalla. No se manda por correo ni queda
// guardada en ningún lado: es una contraseña, y una contraseña que vive
// en un historial deja de serlo.
function ClaveTemporal({ d, onCerrar }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <div onClick={onCerrar} style={{
      position: 'fixed', inset: 0, zIndex: 130, background: 'rgba(10,15,26,.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 22,
        width: '100%', maxWidth: 420,
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: T.navy, marginBottom: 4 }}>
          Clave restablecida
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 14, lineHeight: 1.5 }}>
          {d.nombre} entra con el usuario <strong>{d.usuario}</strong> y esta
          contraseña temporal:
        </div>

        <div style={{
          background: T.warm, borderRadius: 8, padding: '14px 16px',
          textAlign: 'center', marginBottom: 12,
        }}>
          <div style={{
            fontFamily: 'monospace', fontSize: 22, fontWeight: 700,
            color: T.navy, letterSpacing: '.04em', wordBreak: 'break-all',
          }}>
            {d.passwordTemporal}
          </div>
        </div>

        <Boton variante="suave" style={{ width: '100%', marginBottom: 10 }}
               onClick={() => {
                 navigator.clipboard?.writeText(d.passwordTemporal)
                   .then(() => setCopiado(true)).catch(() => {});
               }}>
          {copiado ? 'Copiada' : 'Copiar'}
        </Boton>

        <div style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.5, marginBottom: 14 }}>
          {d.aviso}
        </div>

        <Boton variante="acento" onClick={onCerrar} style={{ width: '100%' }}>
          Listo
        </Boton>
      </div>
    </div>
  );
}

// ── Por qué alguien no puede entrar ─────────────────────────────────
//
// "Sale incorrecta" puede ser cuatro cosas y desde la pantalla de
// acceso se ven todas iguales, a propósito. Esto las separa.
function PorQueNoEntra({ d, onCerrar }) {
  return (
    <div onClick={onCerrar} style={{
      position: 'fixed', inset: 0, zIndex: 130, background: 'rgba(10,15,26,.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 22,
        width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: T.navy, marginBottom: 14 }}>
          {d.nombre}
        </div>

        {d.cargando && <div style={{ fontSize: 13, color: T.muted }}>Revisando…</div>}

        {!d.cargando && (
          <>
            <div style={{ display: 'grid', gap: 7, marginBottom: 16 }}>
              <Dato k="Usuario" v={d.usuario?.username} mono />
              <Dato k="Cuenta"
                    v={d.usuario?.activo ? 'activa' : 'DESACTIVADA'}
                    malo={!d.usuario?.activo} />
              <Dato k="Debe cambiar la clave"
                    v={d.usuario?.requiereCambioPassword ? 'sí' : 'no'} />
              <Dato k="Último ingreso"
                    v={d.usuario?.ultimoLogin ? fmt.fecha(d.usuario.ultimoLogin) : 'nunca'}
                    malo={!d.usuario?.ultimoLogin} />
              <Dato k="Fallidos seguidos" v={String(d.fallidosSeguidos ?? 0)}
                    malo={(d.fallidosSeguidos ?? 0) >= 3} />
            </div>

            {(d.motivos || []).map((m, i) => (
              <div key={i} style={{
                background: T.warnBg, borderLeft: `3px solid ${T.warn}`,
                borderRadius: '0 8px 8px 0', padding: '11px 14px',
                fontSize: 12.5, color: T.text, lineHeight: 1.5, marginBottom: 8,
              }}>
                {m}
              </div>
            ))}

            {(d.historial || []).length > 0 && (
              <>
                <div style={{
                  fontSize: 10, color: T.muted, fontWeight: 700,
                  letterSpacing: '.05em', margin: '16px 0 8px',
                }}>
                  ÚLTIMOS MOVIMIENTOS
                </div>
                <div style={{ display: 'grid', gap: 5 }}>
                  {d.historial.map((h, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: 11.5, gap: 10,
                    }}>
                      <span style={{
                        color: h.accion.includes('fallido') || h.accion.includes('inactiva')
                          ? T.bad : T.muted,
                      }}>
                        {h.accion.replace(/_/g, ' ')}
                      </span>
                      <span style={{ color: T.muted, whiteSpace: 'nowrap' }}>
                        {fmt.fecha(h.cuando)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        <Boton variante="acento" onClick={onCerrar}
               style={{ width: '100%', marginTop: 16 }}>
          Cerrar
        </Boton>
      </div>
    </div>
  );
}

function Dato({ k, v, malo, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}>
      <span style={{ color: T.muted }}>{k}</span>
      <span style={{
        fontWeight: 600,
        color: malo ? T.bad : T.navy,
        fontFamily: mono ? 'monospace' : 'inherit',
      }}>
        {v ?? '—'}
      </span>
    </div>
  );
}
